// POST /api/admin/uploads の薄い client ラッパ。
// multipart/form-data で file フィールドを送り、返却された fileKey を
// thumbnail_key / 画像 URL (`/api/uploads/<fileKey>`) として使う。
export interface UploadResult {
  fileKey: string;
  contentType: string;
  size: number;
}

// 画像を最大サイズにリサイズ (client-side, canvas API)
// 大きな画像 (10MB+) でも 5MB 以下に収まるように自動圧縮
function resizeImage(
  file: File,
  maxSize = 1600,
  quality = 0.85,
): Promise<File> {
  return new Promise((resolve) => {
    // 画像以外はそのまま返す
    if (!file.type.startsWith("image/")) {
      resolve(file);
      return;
    }
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      // リサイズ不要なら元ファイル
      if (width <= maxSize && height <= maxSize && file.size <= 4 * 1024 * 1024) {
        URL.revokeObjectURL(img.src);
        resolve(file);
        return;
      }
      // 縦横比を保ったままスケールダウン
      if (width > height) {
        if (width > maxSize) {
          height = Math.round(height * (maxSize / width));
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = Math.round(width * (maxSize / height));
          height = maxSize;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(img.src);
          if (blob) {
            const ext = file.name.replace(/.*\./, "");
            const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
            resolve(
              new File([blob], name, { type: "image/jpeg" }),
            );
          } else {
            resolve(file);
          }
        },
        "image/jpeg",
        quality,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      resolve(file); // fallback: 元ファイル
    };
    img.src = URL.createObjectURL(file);
  });
}

async function postUpload(endpoint: string, file: File): Promise<UploadResult> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(endpoint, {
    method: "POST",
    body: form,
    credentials: "include",
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const code =
      (data as { error?: { code?: string } } | null)?.error?.code ??
      "upload_error";
    const message =
      (data as { error?: { message?: string } } | null)?.error?.message ??
      `HTTP ${res.status}`;
    const e = new Error(message);
    (e as Error & { code?: string; status?: number }).code = code;
    (e as Error & { code?: string; status?: number }).status = res.status;
    throw e;
  }
  return data as UploadResult;
}

export async function uploadImage(file: File): Promise<UploadResult> {
  const resized = await resizeImage(file);
  return postUpload("/api/admin/uploads", resized);
}

export function uploadPdf(file: File): Promise<UploadResult> {
  return postUpload("/api/admin/uploads/pdf", file);
}

// /api/uploads/<key> 形式の画像 URL を組み立てる。
export function uploadsUrl(fileKey: string | null | undefined): string | null {
  if (!fileKey) return null;
  return `/api/uploads/${fileKey}`;
}
